import React, { useEffect, useState } from "react";
import { fetchNeweggGuide } from "../lib/api";
import { Lightning, CheckCircle, Warning, ArrowSquareOut, Code, Database, ShoppingBag } from "@phosphor-icons/react";

export default function ApiGuide() {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchNeweggGuide().then(setData).catch(() => {});
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="api-guide-page">
      <div className="mb-12">
        <div className="font-mono uppercase text-xs tracking-[0.2em] text-[#002FA7] mb-2">DEVELOPER GUIDE</div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-4">
          How to get Newegg products into <span className="text-[#002FA7]">your store</span>.
        </h1>
        <p className="text-zinc-700 leading-relaxed max-w-2xl">
          Newegg doesn't expose a public consumer product API, but there are three official paths
          and a few unofficial ones. Pick the one that matches your business model.
        </p>
      </div>

      {/* OFFICIAL OPTIONS */}
      <section className="mb-16">
        <div className="flex items-center gap-2 mb-6">
          <CheckCircle size={20} weight="fill" className="text-green-600" />
          <h2 className="font-heading font-bold text-2xl tracking-tight">Official Channels (Recommended)</h2>
        </div>
        <div className="grid gap-4">
          {(data?.official_options || []).map((opt, i) => (
            <div key={i} className="border-2 border-zinc-950 p-6 hard-shadow bg-white" data-testid={`official-option-${i}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="font-mono uppercase text-[10px] tracking-[0.2em] text-green-600 mb-1">
                    OFFICIAL · {opt.requires_approval ? "Requires Approval" : "Open"}
                  </div>
                  <h3 className="font-heading font-bold text-xl mb-2">{opt.name}</h3>
                  <p className="text-zinc-700 leading-relaxed max-w-2xl">{opt.description}</p>
                </div>
                {opt.url && (
                  <a href={opt.url} target="_blank" rel="noreferrer" className="text-xs font-mono uppercase tracking-wider text-[#002FA7] underline hover:text-zinc-950 inline-flex items-center gap-1">
                    Visit <ArrowSquareOut size={12} weight="bold" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* RECOMMENDED FLOW */}
      <section className="mb-16">
        <div className="border-2 border-[#002FA7] bg-[#002FA7] text-white">
          <div className="p-6 border-b border-white/20">
            <div className="font-mono uppercase text-xs tracking-[0.2em] mb-2 flex items-center gap-2">
              <Lightning size={14} weight="fill" /> Recommended Flow
            </div>
            <h2 className="font-heading font-black text-3xl tracking-tight">
              The 5-step affiliate playbook
            </h2>
          </div>
          <ol className="divide-y divide-white/10">
            {(data?.recommended_flow || []).map((step, i) => (
              <li key={i} className="p-6 flex gap-4" data-testid={`flow-step-${i}`}>
                <div className="font-mono font-black text-3xl text-[#FF2A00] w-12 flex-shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="text-base leading-relaxed pt-2">{step.replace(/^\d+\.\s*/, "")}</div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* UNOFFICIAL */}
      <section className="mb-16">
        <div className="flex items-center gap-2 mb-6">
          <Warning size={20} weight="fill" className="text-[#FF2A00]" />
          <h2 className="font-heading font-bold text-2xl tracking-tight">Unofficial Paths (Risky)</h2>
        </div>
        <div className="grid gap-4">
          {(data?.unofficial_options || []).map((opt, i) => (
            <div key={i} className="border-2 border-dashed border-zinc-300 p-6" data-testid={`unofficial-option-${i}`}>
              <div className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#FF2A00] mb-1">
                UNOFFICIAL · USE WITH CAUTION
              </div>
              <h3 className="font-heading font-bold text-xl mb-2">{opt.name}</h3>
              <p className="text-zinc-700 leading-relaxed max-w-2xl mb-3">{opt.description}</p>
              {opt.tools && (
                <div className="flex flex-wrap gap-2">
                  {opt.tools.map((t) => (
                    <span key={t} className="font-mono text-xs uppercase tracking-wider border border-zinc-300 px-2 py-1">
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CODE SAMPLE */}
      <section className="mb-16">
        <div className="flex items-center gap-2 mb-6">
          <Code size={20} weight="bold" />
          <h2 className="font-heading font-bold text-2xl tracking-tight">This app's API (your starting template)</h2>
        </div>
        <div className="bg-zinc-950 text-zinc-100 p-6 font-mono text-sm overflow-x-auto">
          <div className="text-zinc-500 mb-2"># List products (your store catalog)</div>
          <div className="text-green-400">GET</div> <span className="text-zinc-300">{`{API}/api/products?category=GPU&sort=price_asc`}</span>
          <div className="text-zinc-500 mt-4 mb-2"># Get a single product</div>
          <div className="text-green-400">GET</div> <span className="text-zinc-300">{`{API}/api/products/<product_id>`}</span>
          <div className="text-zinc-500 mt-4 mb-2"># Create / Update / Delete (admin)</div>
          <div className="text-orange-400">POST</div> <span className="text-zinc-300">{`{API}/api/products`}</span>
          <div className="text-blue-400 mt-1">PUT</div> <span className="text-zinc-300">{`{API}/api/products/<id>`}</span>
          <div className="text-red-400 mt-1">DELETE</div> <span className="text-zinc-300">{`{API}/api/products/<id>`}</span>
          <div className="text-zinc-500 mt-4 mb-2"># Checkout</div>
          <div className="text-orange-400">POST</div> <span className="text-zinc-300">{`{API}/api/checkout/session`}</span>
          <div className="text-green-400 mt-1">GET</div> <span className="text-zinc-300">{`{API}/api/checkout/status/<session_id>`}</span>
          <div className="text-zinc-500 mt-4 mb-2"># Newegg integration guide JSON</div>
          <div className="text-green-400">GET</div> <span className="text-zinc-300">{`{API}/api/newegg/integration-guide`}</span>
        </div>
      </section>

      {/* SUMMARY */}
      <section className="border-2 border-zinc-950 p-6 bg-zinc-50">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <Database size={28} weight="duotone" className="mb-3 text-[#002FA7]" />
            <h4 className="font-heading font-bold text-lg mb-1">1. Get the feed</h4>
            <p className="text-sm text-zinc-700">Apply for Newegg affiliate via Impact, download daily CSV/XML feeds.</p>
          </div>
          <div>
            <Code size={28} weight="duotone" className="mb-3 text-[#002FA7]" />
            <h4 className="font-heading font-bold text-lg mb-1">2. Sync to MongoDB</h4>
            <p className="text-sm text-zinc-700">Run a cron job that POSTs to <span className="font-mono text-xs">/api/products</span>.</p>
          </div>
          <div>
            <ShoppingBag size={28} weight="duotone" className="mb-3 text-[#002FA7]" />
            <h4 className="font-heading font-bold text-lg mb-1">3. Sell + earn</h4>
            <p className="text-sm text-zinc-700">Stripe checkout or redirect with your affiliate ID for commission.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
